import { useState, useCallback, useRef } from "react";

const BATCH_SIZE = 20;

const CATEGORIES = {
  WRONG_SCHEME:     "wrongScheme",
  WRONG_EMAIL:      "wrongEmail",
  PERSONAL_DETAILS: "personalDetails",
  OTHER:            "other",
};

const CATEGORY_LABELS = {
  [CATEGORIES.WRONG_SCHEME]:      "Staff claiming wrong pension scheme (should be Local Government)",
  [CATEGORIES.WRONG_EMAIL]:       "Staff claiming email sent to wrong address",
  [CATEGORIES.PERSONAL_DETAILS]:  "Staff requesting personal detail changes (email / address)",
  [CATEGORIES.OTHER]:             "Other / Uncategorised",
};

const CATEGORY_COLORS = {
  [CATEGORIES.WRONG_SCHEME]:      { bg: "#FFF3E0", border: "#F57C00", badge: "#E65100", text: "#BF360C" },
  [CATEGORIES.WRONG_EMAIL]:       { bg: "#E3F2FD", border: "#1565C0", badge: "#0D47A1", text: "#0A2F6B" },
  [CATEGORIES.PERSONAL_DETAILS]:  { bg: "#E8F5E9", border: "#2E7D32", badge: "#1B5E20", text: "#0D3B11" },
  [CATEGORIES.OTHER]:             { bg: "#F3E5F5", border: "#6A1B9A", badge: "#4A148C", text: "#2D0060" },
};

const S = {
  root:         { fontFamily: "'Georgia','Times New Roman',serif", background: "#F7F4EF", minHeight: "100vh", color: "#1A1208" },
  header:       { background: "#1A1208", color: "#F7F4EF", padding: "28px 36px 24px", borderBottom: "3px solid #C9A84C" },
  headerTop:    { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "16px", flexWrap: "wrap" },
  badge:        { display: "inline-block", fontSize: "10px", fontFamily: "'Courier New',monospace", fontWeight: "700", letterSpacing: "2px", textTransform: "uppercase", color: "#C9A84C", border: "1px solid #C9A84C", padding: "3px 10px", borderRadius: "2px", marginBottom: "10px" },
  title:        { margin: "0 0 6px", fontSize: "26px", fontWeight: "700", letterSpacing: "-0.3px", lineHeight: "1.2" },
  subtitle:     { margin: "0", fontSize: "13px", color: "#A89060", fontStyle: "italic" },
  body:         { padding: "28px 36px", maxWidth: "1100px" },
  card:         { background: "#FFFFFF", border: "1px solid #E0D8CC", borderRadius: "6px", padding: "24px", marginBottom: "20px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" },
  controlRow:   { display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" },
  btn:          { display: "inline-flex", alignItems: "center", gap: "8px", padding: "11px 22px", border: "none", borderRadius: "4px", fontSize: "14px", fontFamily: "'Georgia',serif", fontWeight: "600", cursor: "pointer", letterSpacing: "0.2px" },
  btnPrimary:   { background: "#1A1208", color: "#F7F4EF" },
  btnSecondary: { background: "#F7F4EF", color: "#1A1208", border: "1px solid #C8BFA8" },
  btnDanger:    { background: "#C62828", color: "#FFF" },
  btnExport:    { background: "#2E7D32", color: "#FFF" },
  btnUpload:    { background: "#1565C0", color: "#FFF" },
  uploadBox:    { border: "2px dashed #C9A84C", borderRadius: "6px", padding: "32px", textAlign: "center", background: "#FDFAF4", cursor: "pointer", marginBottom: "16px" },
  uploadIcon:   { fontSize: "36px", marginBottom: "10px" },
  uploadText:   { fontSize: "14px", color: "#6B5C3E", fontStyle: "italic" },
  uploadSub:    { fontSize: "12px", color: "#8C7B5E", marginTop: "6px" },
  progressWrap: { marginTop: "16px" },
  progressBar:  { height: "6px", background: "#E0D8CC", borderRadius: "3px", overflow: "hidden", marginTop: "8px" },
  progressFill: { height: "100%", background: "#C9A84C", borderRadius: "3px", transition: "width 0.3s ease" },
  progressText: { fontSize: "12px", fontFamily: "'Courier New',monospace", color: "#6B5C3E", marginTop: "6px" },
  summaryGrid:  { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: "14px", marginBottom: "24px" },
  summaryCard:  { borderRadius: "5px", padding: "16px 18px", borderLeft: "4px solid", cursor: "pointer" },
  summaryCount: { fontSize: "32px", fontWeight: "700", lineHeight: "1", marginBottom: "4px" },
  summaryLabel: { fontSize: "12px", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.8px", opacity: 0.75 },
  sectionTitle: { fontSize: "15px", fontWeight: "700", letterSpacing: "0.5px", textTransform: "uppercase", color: "#1A1208", borderBottom: "2px solid #E0D8CC", paddingBottom: "8px", marginBottom: "14px", marginTop: "0", display: "flex", alignItems: "center", gap: "10px" },
  table:        { width: "100%", borderCollapse: "collapse", fontSize: "13px" },
  th:           { textAlign: "left", padding: "9px 12px", fontFamily: "'Courier New',monospace", fontSize: "11px", letterSpacing: "1px", textTransform: "uppercase", color: "#6B5C3E", borderBottom: "2px solid #E0D8CC", fontWeight: "700", background: "#FAF8F4" },
  td:           { padding: "10px 12px", borderBottom: "1px solid #F0EBE1", verticalAlign: "top", lineHeight: "1.45" },
  catBadge:     { display: "inline-block", fontSize: "10px", fontWeight: "700", letterSpacing: "0.8px", textTransform: "uppercase", padding: "2px 8px", borderRadius: "2px", color: "#FFF" },
  logBox:       { background: "#1A1208", color: "#A89060", fontFamily: "'Courier New',monospace", fontSize: "11px", padding: "14px", borderRadius: "4px", maxHeight: "160px", overflowY: "auto", marginTop: "12px", lineHeight: "1.7" },
  errorBox:     { background: "#FFEBEE", border: "1px solid #EF9A9A", borderRadius: "4px", padding: "12px 16px", color: "#B71C1C", fontSize: "13px", marginTop: "12px" },
  emptyState:   { textAlign: "center", padding: "40px 20px", color: "#8C7B5E", fontStyle: "italic", fontSize: "14px" },
  tabRow:       { display: "flex", gap: "0", borderBottom: "2px solid #E0D8CC", marginBottom: "18px", flexWrap: "wrap" },
  tab:          { padding: "9px 18px", fontSize: "12px", fontWeight: "700", fontFamily: "'Courier New',monospace", letterSpacing: "0.8px", textTransform: "uppercase", border: "none", borderBottom: "3px solid transparent", background: "none", cursor: "pointer", color: "#8C7B5E", marginBottom: "-2px" },
  tabActive:    { color: "#1A1208", borderBottomColor: "#C9A84C" },
  infoBox:      { background: "#E3F2FD", border: "1px solid #90CAF9", borderRadius: "4px", padding: "12px 16px", fontSize: "13px", color: "#0D47A1", marginBottom: "16px", lineHeight: "1.6" },
};

// ─── Extract just the staff reply from a threaded email body ──────────────────
// Thread structure in CSV:
//   [Andrew's reply at top — optional, only in 3-way threads]
//   [Staff message in middle — THIS IS WHAT WE WANT]
//   [Andrew's original sending email at bottom]
function extractStaffMessage(body) {
  // Step 1: Cut off Andrew's original email at the bottom
  // These patterns mark where the original "Dear STAFF MEMBER" email begins
  const bottomPatterns = [
    /\nOn \w{3},?\s+\d+\s+\w+\s+\d{4}[^)]*andrew redmond[^)]*wrote:/i,
    /\nOn \w{3} \d+\s+\w+\s+\d{4}[^)]*andrew redmond[^)]*wrote:/i,
    /_{10,}[\r\n]+From: andrew redmond/i,
    /From: andrew redmond[^\n]*\nSent:/i,
    /From: andrew redmond \[mailto:/i,
  ];

  let bottomCut = body.length;
  for (const pattern of bottomPatterns) {
    const m = body.match(pattern);
    if (m && m.index < bottomCut) bottomCut = m.index;
  }
  let upper = body.slice(0, bottomCut).trim();

  // Step 2: If Andrew replied at the top (3-way thread), find the staff message
  // after his reply. It's introduced by a "From: Staff Name\nSent:" header block.
  const fromStaff = upper.match(
    /\nFrom:\s+(?!andrew redmond)([^\n]+)\nSent:[^\n]+\nTo:[^\n]+\nSubject:[^\n]+\n/i
  );
  if (fromStaff) {
    const afterHeader = upper.slice(fromStaff.index + fromStaff[0].length).trim();
    if (afterHeader.length > 20) return afterHeader;
  }

  // No nested header — upper section IS the staff message
  return upper;
}

// ─── CSV Parser ───────────────────────────────────────────────────────────────
function parseOutlookCSV(text) {
  if (text.charCodeAt(0) === 0xFEFF) text = text.slice(1);

  let i = 0;
  function field() {
    if (text[i] === '"') {
      i++;
      let v = "";
      while (i < text.length) {
        if (text[i] === '"' && text[i + 1] === '"') { v += '"'; i += 2; }
        else if (text[i] === '"') { i++; break; }
        else v += text[i++];
      }
      return v;
    }
    let v = "";
    while (i < text.length && text[i] !== ',' && text[i] !== '\n' && text[i] !== '\r') v += text[i++];
    return v;
  }
  function line() {
    const fs = [];
    while (i < text.length && text[i] !== '\n' && text[i] !== '\r') {
      fs.push(field());
      if (text[i] === ',') i++;
    }
    if (text[i] === '\r') i++;
    if (text[i] === '\n') i++;
    return fs;
  }

  const headers = line();
  const col = (name) => headers.findIndex(h => h.trim() === name);
  const subjectIdx  = col('Subject');
  const bodyIdx     = col('Body');
  const fromNameIdx = col('From: (Name)');
  const fromAddrIdx = col('From: (Address)');

  const allRows = [];
  while (i < text.length) {
    const fs = line();
    if (fs.length < 2) continue;
    allRows.push({
      subject:    fs[subjectIdx]  || '',
      body:       fs[bodyIdx]     || '',
      from_name:  fs[fromNameIdx] || '',
      from_email: fs[fromAddrIdx] || '',
    });
  }

  // Resolve real sender + extract staff message
  // No deduplication — keep all emails so no query gets missed
  const emails = [];
  const skipBodies = new Set();

  for (const row of allRows) {
    let fromEmail = row.from_email.trim();
    let fromName  = row.from_name.trim();
    let body      = row.body;

    const isExchange = /^\/o=exchangelabs/i.test(fromEmail) || /^\/O=EXCHANGELABS/.test(fromEmail);

    if (isExchange) {
      // Sent Items format — extract from body
      const emailMatch = body.match(/Original sender:\s*([^\s\r\n]+)/);
      const nameMatch  = body.match(/Forwarded email from:\s*([^\r\n]+)/);
      if (!emailMatch) continue;
      fromEmail = emailMatch[1].trim();
      fromName  = nameMatch ? nameMatch[1].trim() : '';
      const folderMatch = body.match(/Original folder:[^\r\n]*[\r\n]+/);
      if (folderMatch) {
        const after = body.indexOf(folderMatch[0]) + folderMatch[0].length;
        body = body.slice(after).replace(/^[\s_\-]+/, '').trim();
      }
    }

    if (!fromEmail || !fromEmail.includes('@')) continue;
    if (/andrewredmond/i.test(fromEmail)) continue;
    if (/boxbe-notifications/i.test(fromEmail)) continue;

    // Extract just the staff message from the thread
    const staffMessage = extractStaffMessage(body);

    // Skip empty or very short messages
    if (staffMessage.length < 10) continue;

    // Skip exact duplicate bodies (same person, same message sent twice)
    const bodyKey = fromEmail.toLowerCase() + '|' + staffMessage.slice(0, 100);
    if (skipBodies.has(bodyKey)) continue;
    skipBodies.add(bodyKey);

    emails.push({
      from_name:  fromName,
      from_email: fromEmail,
      subject:    row.subject.replace(/^(FW:|RE:|Re:|Fw:)\s*/gi, '').trim(),
      body:       staffMessage.slice(0, 800),
    });
  }

  return emails;
}

// ─── Classification prompt ────────────────────────────────────────────────────
function buildPrompt(batch) {
  const list = batch.map((e, i) => `--- EMAIL ${i + 1} ---
From: ${e.from_name} <${e.from_email}>
Subject: ${e.subject}
Message: ${e.body}`).join("\n\n");

  return `You are classifying staff replies to a hospital pension team. Each email is a reply to an Annual Benefit Statement for the Single Public Service Pension Scheme (SPPS/SPSPS) sent by Beaumont Hospital.

Classify EACH email into exactly ONE of these four categories. Read carefully — many emails contain subtle signals.

WRONG_SCHEME: The person believes they should NOT be on the Single Public Service Pension Scheme at all. They think they belong to an older/different scheme.
Signals: "local government scheme", "superannuation scheme", "pre-2013", "pre 2013", "started before 2013", "career break", "never took a break", "not a member of SPSS/SPSPS", "enrolled on local government", "earlier scheme", "old scheme", "correct pension", "put me back on", "confirmed I was on the pre 2013", "superannuation not single scheme".
Examples:
- "I am not a member of the SPSS. I am enrolled on the local government superannuation scheme."
- "I have been working since July 2011 and have never taken any break. As far as I am aware I am not supposed to be on the single service pension scheme."
- "My understanding is that I am in the earlier superannuation scheme and not the single public service pension scheme."
- "I enrolled pre-2013 so am part of the Superannuation scheme not Single Pension scheme."
- "Please let me know if anything else is needed to put me back on the correct pension pre 2013."
- "I have contacted pensions several times to confirm I was on the pre 2013 pension which they confirmed."

WRONG_EMAIL: The email was sent to the wrong person or wrong address. The person does not work at Beaumont, works at a completely different organisation, or is clearly not the intended recipient.
Signals: "wrong person", "wrong address", "don't work at Beaumont", "no longer work", "work at a different", "not the right person", "retired", automated reply from non-Beaumont organisation.
Examples:
- "I don't work at Beaumont Hospital. I think this was sent to me in error."
- Auto-reply from someone at alfred.org.au (Australian hospital) or wustl.edu (US university).
- "Dr Moorhouse is no longer seeing consultations" (from Bon Secours, not Beaumont).
Note: Staff who still work at Beaumont but query the scheme are NOT wrong email.

PERSONAL_DETAILS: The person is requesting a change to their personal details stored on the system.
Signals: "update my address", "change my address", "new address is", "update my email", "change my email", "new email", "update my details", "my address has changed", "address is incorrect", "please amend", "please update", "could you update", "new home address".
Examples:
- "My address is 439 Railway Terrace, Sallins, Kildare."
- "Could my email be updated to liammulcahy@proton.me and my address to 18 Sunbury Park."
- "I noticed my home address is not up to date. Can you please change to 11 Hillfield."
- "I received my annual benefit statement. My address is incorrect. Can I change my address to 33 Hampton."
- "I wanted to send my new home address for correspondence purposes. My new address is 99 Ferndale Avenue."

OTHER: Everything else — general pension queries, transfer questions, cashing in pension, AVC queries, portal technical issues, out-of-office auto-replies from Beaumont staff, thank-you replies, questions about missing years, queries about contributions, etc.

IMPORTANT RULES:
- If an email mentions BOTH wrong scheme AND address change, classify as WRONG_SCHEME (more actionable).
- Out-of-office replies from Beaumont/HSE/RCSI staff = OTHER (they still work there).
- Out-of-office or auto-replies from completely unrelated organisations = WRONG_EMAIL.
- Short thank-you replies ("Thanks", "Received") = OTHER.
- Questions about pension value, transfers, missing years = OTHER.

Reply ONLY with a JSON array, no markdown, no explanation:
[{"index":1,"category":"WRONG_SCHEME"|"WRONG_EMAIL"|"PERSONAL_DETAILS"|"OTHER","name":"sender full name","email":"sender email","summary":"one sentence"}]

Emails to classify:
${list}`;
}

function catKey(cat) {
  return { WRONG_SCHEME: CATEGORIES.WRONG_SCHEME, WRONG_EMAIL: CATEGORIES.WRONG_EMAIL, PERSONAL_DETAILS: CATEGORIES.PERSONAL_DETAILS, OTHER: CATEGORIES.OTHER }[cat] || CATEGORIES.OTHER;
}
function nameFromEmail(email) {
  return (email || '').split('@')[0].replace(/[._-]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function SingleSchemeQueryAnalyser() {
  const [emails, setEmails]       = useState([]);
  const [fileName, setFileName]   = useState("");
  const [running, setRunning]     = useState(false);
  const [progress, setProgress]   = useState(0);
  const [total, setTotal]         = useState(0);
  const [logs, setLogs]           = useState([]);
  const [results, setResults]     = useState([]);
  const [error, setError]         = useState(null);
  const [activeTab, setActiveTab] = useState(CATEGORIES.WRONG_SCHEME);
  const cancelRef = useRef(false);
  const fileRef   = useRef(null);
  const logRef    = useRef(null);

  const log = (msg) => setLogs(l => {
    const next = [...l, `[${new Date().toLocaleTimeString()}] ${msg}`];
    setTimeout(() => { if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight; }, 50);
    return next;
  });

  const reset = () => {
    setRunning(false); cancelRef.current = false;
    setProgress(0); setTotal(0); setLogs([]); setResults([]); setError(null);
  };
  const fullReset = () => { reset(); setEmails([]); setFileName(""); };

  const handleFile = (file) => {
    if (!file) return;
    if (!file.name.endsWith('.csv')) { setError("Please upload a .csv file exported from Outlook."); return; }
    setError(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const parsed = parseOutlookCSV(e.target.result);
        if (!parsed.length) { setError("No valid staff emails found. Make sure this is a CSV exported from Outlook."); return; }
        setEmails(parsed); setFileName(file.name); setResults([]); setLogs([]);
        log(`✓ ${parsed.length} unique staff emails loaded from ${file.name}`);
      } catch (err) { setError("Failed to parse CSV: " + err.message); }
    };
    reader.readAsText(file, 'utf-8');
  };

  const classifyBatch = async (batch) => {
    const res = await fetch("/api/server", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        model: "claude-sonnet-4-5",
        max_tokens: 1000,
        messages: [{ role: "user", content: buildPrompt(batch) }],
      }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(`API ${res.status}: ${err.error?.message || res.statusText}`);
    }
    const data = await res.json();
    const text = data.content?.filter(b => b.type === "text").map(b => b.text).join("") || "";
    const match = text.match(/\[[\s\S]*\]/);
    if (!match) return batch.map((e, i) => ({ index: i + 1, category: "OTHER", name: e.from_name || nameFromEmail(e.from_email), email: e.from_email, summary: e.subject }));
    try { return JSON.parse(match[0]); } catch (_) { return []; }
  };

  const run = useCallback(async () => {
    if (!emails.length) { setError("Please upload a CSV file first."); return; }
    reset();
    setRunning(true);
    setTotal(emails.length);
    const totalBatches = Math.ceil(emails.length / BATCH_SIZE);
    log(`Classifying ${emails.length} emails in ${totalBatches} batches...`);
    try {
      let all = [];
      for (let i = 0; i < emails.length; i += BATCH_SIZE) {
        if (cancelRef.current) { log("Stopped."); break; }
        const batch = emails.slice(i, i + BATCH_SIZE);
        const bn = Math.floor(i / BATCH_SIZE) + 1;
        log(`Batch ${bn}/${totalBatches} — emails ${i + 1}–${Math.min(i + BATCH_SIZE, emails.length)}...`);
        const classified = await classifyBatch(batch);
        all = [...all, ...classified];
        setResults([...all]);
        setProgress(Math.min(i + BATCH_SIZE, emails.length));
        const ws = classified.filter(c => c.category === "WRONG_SCHEME").length;
        const we = classified.filter(c => c.category === "WRONG_EMAIL").length;
        const pd = classified.filter(c => c.category === "PERSONAL_DETAILS").length;
        log(`✓ Batch ${bn} — ${ws} wrong scheme, ${we} wrong email, ${pd} personal details`);
        if (i + BATCH_SIZE < emails.length) await new Promise(r => setTimeout(r, 500));
      }
      const ws = all.filter(c => c.category === "WRONG_SCHEME").length;
      const we = all.filter(c => c.category === "WRONG_EMAIL").length;
      const pd = all.filter(c => c.category === "PERSONAL_DETAILS").length;
      log(`✅ Complete — ${all.length} emails: ${ws} wrong scheme, ${we} wrong email, ${pd} personal details`);
    } catch (err) {
      setError(err.message);
      log(`❌ ${err.message}`);
    } finally { setRunning(false); }
  }, [emails, cancelRef]);

  const mkCSV = (rows) => [
    ["Name","Email","Category","Summary"].join(","),
    ...rows.map(r => [
      `"${(r.name||"").replace(/"/g,'""')}"`,
      `"${(r.email||"").replace(/"/g,'""')}"`,
      `"${CATEGORY_LABELS[catKey(r.category)]||r.category}"`,
      `"${(r.summary||"").replace(/"/g,'""')}"`,
    ].join(","))
  ].join("\n");

  const download = (csv, name) => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = name; a.click();
  };

  const counts = Object.values(CATEGORIES).reduce((acc, cat) => {
    acc[cat] = results.filter(r => catKey(r.category) === cat).length; return acc;
  }, {});
  const filtered = results.filter(r => catKey(r.category) === activeTab);
  const pct = total > 0 ? Math.round((progress / total) * 100) : 0;

  return (
    <div style={S.root}>
      <div style={S.header}>
        <div style={S.headerTop}>
          <div>
            <div style={S.badge}>Administrator Tool</div>
            <h1 style={S.title}>Single Scheme Query Analyser</h1>
            <p style={S.subtitle}>Upload Outlook CSV export → AI classifies all staff emails automatically</p>
          </div>
          {results.length > 0 && (
            <button style={{ ...S.btn, ...S.btnExport }} onClick={() => download(mkCSV(results), `single_scheme_all_${new Date().toISOString().slice(0,10)}.csv`)}>
              ↓ Export All CSV
            </button>
          )}
        </div>
      </div>

      <div style={S.body}>
        {emails.length === 0 && (
          <div style={S.infoBox}>
            <strong>How to export from Outlook:</strong> File → Open &amp; Export → Import/Export → Export to a file → Comma Separated Values → select the Queries folder → Save. Upload the resulting .csv below.
          </div>
        )}

        <div style={S.card}>
          {emails.length === 0 ? (
            <div style={S.uploadBox}
              onClick={() => fileRef.current?.click()}
              onDrop={e => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}
              onDragOver={e => e.preventDefault()}
            >
              <div style={S.uploadIcon}>📂</div>
              <div style={S.uploadText}>Click to select or drag &amp; drop your Outlook CSV export</div>
              <div style={S.uploadSub}>Works with CSV exported from any Outlook mail folder</div>
            </div>
          ) : (
            <div style={{ marginBottom: "16px", display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
              <span style={{ fontSize: "13px", color: "#2E7D32", fontWeight: "600" }}>
                ✓ {fileName} — {emails.length} unique staff emails loaded
              </span>
              <button style={{ ...S.btn, ...S.btnSecondary, padding: "6px 14px", fontSize: "12px" }} onClick={fullReset}>✕ Remove</button>
            </div>
          )}

          <input ref={fileRef} type="file" accept=".csv" style={{ display: "none" }} onChange={e => handleFile(e.target.files[0])} />

          <div style={S.controlRow}>
            {emails.length === 0 && <button style={{ ...S.btn, ...S.btnUpload }} onClick={() => fileRef.current?.click()}>📂 Upload CSV</button>}
            {emails.length > 0 && !running && <button style={{ ...S.btn, ...S.btnPrimary }} onClick={run}>▶ Run Analysis</button>}
            {running && <button style={{ ...S.btn, ...S.btnDanger }} onClick={() => { cancelRef.current = true; }}>■ Stop</button>}
            {results.length > 0 && !running && (
              <>
                <button style={{ ...S.btn, ...S.btnSecondary }} onClick={reset}>↺ Re-run</button>
                <span style={{ fontSize: "13px", color: "#6B5C3E", fontStyle: "italic" }}>{results.length} classified</span>
              </>
            )}
          </div>

          {(running || progress > 0) && (
            <div style={S.progressWrap}>
              <div style={S.progressBar}><div style={{ ...S.progressFill, width: `${pct}%` }} /></div>
              <div style={S.progressText}>{progress} / {total} emails ({pct}%)</div>
            </div>
          )}

          {logs.length > 0 && <div style={S.logBox} ref={logRef}>{logs.map((l, i) => <div key={i}>{l}</div>)}</div>}
          {error && <div style={S.errorBox}>⚠ {error}</div>}
        </div>

        {results.length > 0 && (
          <>
            <div style={S.summaryGrid}>
              {Object.values(CATEGORIES).map(cat => {
                const col = CATEGORY_COLORS[cat];
                return (
                  <div key={cat}
                    style={{ ...S.summaryCard, background: col.bg, borderLeftColor: col.border, outline: activeTab === cat ? `2px solid ${col.border}` : "none" }}
                    onClick={() => setActiveTab(cat)}
                  >
                    <div style={{ ...S.summaryCount, color: col.text }}>{counts[cat]}</div>
                    <div style={{ ...S.summaryLabel, color: col.text }}>
                      {cat === CATEGORIES.WRONG_SCHEME && "Wrong Scheme"}
                      {cat === CATEGORIES.WRONG_EMAIL && "Wrong Email"}
                      {cat === CATEGORIES.PERSONAL_DETAILS && "Personal Details"}
                      {cat === CATEGORIES.OTHER && "Other"}
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={S.tabRow}>
              {Object.values(CATEGORIES).map(cat => (
                <button key={cat} style={{ ...S.tab, ...(activeTab === cat ? S.tabActive : {}) }} onClick={() => setActiveTab(cat)}>
                  {cat === CATEGORIES.WRONG_SCHEME && "Wrong Scheme"}
                  {cat === CATEGORIES.WRONG_EMAIL && "Wrong Email"}
                  {cat === CATEGORIES.PERSONAL_DETAILS && "Personal Details"}
                  {cat === CATEGORIES.OTHER && "Other"}
                  {" "}({counts[cat]})
                </button>
              ))}
            </div>

            <div style={S.card}>
              <h3 style={S.sectionTitle}>
                <span style={{ ...S.catBadge, background: CATEGORY_COLORS[activeTab].badge }}>{counts[activeTab]}</span>
                {CATEGORY_LABELS[activeTab]}
              </h3>
              {filtered.length === 0 ? (
                <div style={S.emptyState}>No emails in this category.</div>
              ) : (
                <>
                  <div style={{ marginBottom: "14px" }}>
                    <button style={{ ...S.btn, ...S.btnExport, padding: "7px 16px", fontSize: "12px" }}
                      onClick={() => download(mkCSV(filtered), `single_scheme_${activeTab}_${new Date().toISOString().slice(0,10)}.csv`)}>
                      ↓ Export this list
                    </button>
                  </div>
                  <div style={{ overflowX: "auto" }}>
                    <table style={S.table}>
                      <thead>
                        <tr>
                          <th style={S.th}>#</th>
                          <th style={S.th}>Name</th>
                          <th style={S.th}>Email</th>
                          <th style={S.th}>Summary</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.map((r, i) => (
                          <tr key={i} style={{ background: i % 2 === 0 ? "#FFFFFF" : "#FAF8F4" }}>
                            <td style={{ ...S.td, color: "#8C7B5E", fontFamily: "'Courier New',monospace", fontSize: "11px", width: "36px" }}>{i + 1}</td>
                            <td style={{ ...S.td, fontWeight: "600", whiteSpace: "nowrap" }}>{r.name || nameFromEmail(r.email)}</td>
                            <td style={{ ...S.td, fontFamily: "'Courier New',monospace", fontSize: "12px", color: "#4A3F2E" }}>{r.email || "—"}</td>
                            <td style={{ ...S.td, color: "#3D3020" }}>{r.summary || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
